import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Heart, Users, UserPlus, CheckCircle, Lock, ShieldCheck } from 'lucide-react';
const totalSteps = 3;
const stepAnim = {
  initial: {
    opacity: 0,
    x: 20
  },
  animate: {
    opacity: 1,
    x: 0
  },
  exit: {
    opacity: 0,
    x: -20
  },
  transition: {
    duration: 0.3
  }
};
const namePattern = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-]{2,}$/;
export function HeroSection() {
  const [step, setStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [nameErrors, setNameErrors] = useState({
    firstName: '',
    lastName: ''
  });
  const [formData, setFormData] = useState({
    beneficiary: '',
    dobMonth: '',
    dobDay: '',
    dobYear: '',
    firstName: '',
    lastName: '',
    country: 'US',
    phone: '',
    email: ''
  });
  const fbTrack = (event: string, data?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', event, data);
    }
  };
  const handleBeneficiarySelect = (value: string) => {
    setFormData({
      ...formData,
      beneficiary: value
    });
    fbTrack('FormStep1_Beneficiary', {
      beneficiary: value
    });
    setStep(2);
  };
  const handleDobSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fbTrack('FormStep2_DOB', {
      dob: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`
    });
    setStep(3);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate names
    const errors = {
      firstName: '',
      lastName: ''
    };
    if (!namePattern.test(formData.firstName.trim())) {
      errors.firstName = 'Ingresa un nombre válido (mínimo 2 letras)';
    }
    if (!namePattern.test(formData.lastName.trim())) {
      errors.lastName = 'Ingresa un apellido válido (mínimo 2 letras)';
    }
    setNameErrors(errors);
    if (errors.firstName || errors.lastName) return;
    // Track
    fbTrack('FormStep3_Contact', {
      firstName: formData.firstName,
      lastName: formData.lastName,
      country: formData.country,
      email: formData.email
    });
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: 'IUL Insurance Lead',
        beneficiary: formData.beneficiary,
        country: formData.country
      });
    }
    // Send to webhook
    try {
      await fetch('https://services.leadconnectorhq.com/hooks/mrY27qmBeUjAywvVPRmw/webhook-trigger/7c9ac155-7c0b-4913-98c8-791c80bdfa07', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          beneficiary: formData.beneficiary,
          date_of_birth: `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`,
          first_name: formData.firstName,
          last_name: formData.lastName,
          country: formData.country,
          phone: formData.phone,
          email: formData.email
        })
      });
    } catch (err) {
      // submit silently
    }
    setIsSubmitted(true);
  };
  const isCompact = step === 3;
  return <>
      <section className={`relative bg-navy-900 text-white overflow-visible ${isCompact ? 'py-3 sm:py-6 lg:py-10' : 'py-6 sm:py-10 lg:py-16'}`} data-id="element-10">
        {/* Background Image */}
        <div className="absolute inset-0 pointer-events-none" data-id="element-11">
          <img src="https://cdn.magicpatterns.com/uploads/9kCNs6mfkPXqJqJxWLt3LF/happy-and-affectionate-family-members-2024-03-05-23-41-56-utc.jpg" alt="" className="w-full h-full object-cover opacity-[0.25]" data-id="element-12" />
          <div className="absolute inset-0 bg-navy-900/70" data-id="element-13"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-4xl text-center" data-id="element-14">
          {/* Headlines */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className={isCompact ? 'mb-2 sm:mb-4' : 'mb-5 sm:mb-8'} data-id="element-15">
            <h1 className={`font-bold leading-tight max-w-3xl mx-auto ${isCompact ? 'text-lg sm:text-2xl lg:text-3xl mb-1 sm:mb-2' : 'text-2xl sm:text-4xl lg:text-5xl mb-2 sm:mb-4'}`} data-id="element-16">
              <span className="uppercase block text-[#43B0E7]" data-id="element-17">
                ¡Ahorra para tu retiro!
              </span>
              <span className="text-white" data-id="element-18">
                Si algo te pasa, tu familia recibe lo que necesita
              </span>
            </h1>
            {!isCompact && <p className="text-sm sm:text-lg text-slate-300 max-w-xl mx-auto leading-snug" data-id="element-19">
                El IUL combina inversión + seguro de vida en un solo plan. Sin
                tener que elegir entre uno u otro.
              </p>}
          </motion.div>

          {/* Multi-step Form Card */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl text-slate-900" data-id="element-20">
            {isSubmitted ? <div className="p-8 text-center" data-id="element-21">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-22">
                  <CheckCircle className="w-8 h-8" data-id="element-23" />
                </motion.div>
                <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-24">
                  ¡Gracias!
                </h3>
                <p className="text-slate-600" data-id="element-25">
                  Hemos recibido tu información. Un especialista se pondrá en
                  contacto contigo en menos de 24 horas.
                </p>
              </div> : <div className={isCompact ? 'p-3 sm:p-5' : 'p-4 sm:p-6'} data-id="element-26">
                {/* Form headline + helper — only on step 1 */}
                {step === 1 && <div className="mb-4 text-center" data-id="element-27">
                    <h2 className="text-base sm:text-lg font-bold text-navy-900 mb-1" data-id="element-28">
                      Recibe tu plan personalizado en minutos
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-500" data-id="element-29">
                      Un asesor experto te contactará en menos de 24 horas con
                      opciones sin costo.
                    </p>
                  </div>}

                {/* Progress Indicator */}
                <div className={isCompact ? 'mb-2.5' : 'mb-4'} data-id="element-30">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 text-center" data-id="element-31">
                    Paso {step} de {totalSteps}
                  </div>
                  <div className="flex gap-1.5 justify-center" data-id="element-32">
                    {Array.from({
                  length: totalSteps
                }, (_, i) => i + 1).map(i => <div key={i} className={`h-1.5 rounded-full flex-1 transition-colors duration-300 ${i <= step ? 'bg-brandBlue-500' : 'bg-slate-100'}`} data-id="element-33"></div>)}
                  </div>
                </div>

                {/* Step Content */}
                <AnimatePresence mode="wait" data-id="element-34">
                  {/* STEP 1 — Beneficiary */}
                  {step === 1 && <motion.div key="step1" {...stepAnim} data-id="element-35">
                      <h2 className="text-lg sm:text-xl font-bold text-navy-900 mb-4" data-id="element-36">
                        ¿Quién sería tu beneficiario?
                      </h2>
                      <div className="grid grid-cols-3 gap-2 sm:gap-4" data-id="element-37">
                        <button onClick={() => handleBeneficiarySelect('Esposo/a')} className="flex flex-col items-center justify-center p-3 sm:p-5 border-2 border-slate-100 rounded-xl hover:border-brandBlue-500 hover:bg-brandBlue-50 transition-all group" data-id="element-38">
                          <Heart className="w-7 h-7 sm:w-10 sm:h-10 text-slate-400 group-hover:text-brandBlue-500 mb-2 transition-colors" data-id="element-39" />
                          <span className="font-semibold text-xs sm:text-sm text-slate-700 group-hover:text-brandBlue-700" data-id="element-40">
                            Esposo/a
                          </span>
                        </button>
                        <button onClick={() => handleBeneficiarySelect('Hijos')} className="flex flex-col items-center justify-center p-3 sm:p-5 border-2 border-slate-100 rounded-xl hover:border-brandBlue-500 hover:bg-brandBlue-50 transition-all group" data-id="element-41">
                          <Users className="w-7 h-7 sm:w-10 sm:h-10 text-slate-400 group-hover:text-brandBlue-500 mb-2 transition-colors" data-id="element-42" />
                          <span className="font-semibold text-xs sm:text-sm text-slate-700 group-hover:text-brandBlue-700" data-id="element-43">
                            Hijos
                          </span>
                        </button>
                        <button onClick={() => handleBeneficiarySelect('Otro')} className="flex flex-col items-center justify-center p-3 sm:p-5 border-2 border-slate-100 rounded-xl hover:border-brandBlue-500 hover:bg-brandBlue-50 transition-all group" data-id="element-44">
                          <UserPlus className="w-7 h-7 sm:w-10 sm:h-10 text-slate-400 group-hover:text-brandBlue-500 mb-2 transition-colors" data-id="element-45" />
                          <span className="font-semibold text-xs sm:text-sm text-slate-700 group-hover:text-brandBlue-700" data-id="element-46">
                            Otro
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-slate-400" data-id="element-47">
                        <Lock className="w-3 h-3" data-id="element-48" />
                        <span data-id="element-49">
                          100% gratis. Sin compromisos. Tus datos están seguros.
                        </span>
                      </div>
                    </motion.div>}

                  {/* STEP 2 — Date of Birth */}
                  {step === 2 && <motion.div key="step2" {...stepAnim} data-id="element-50">
                      <h2 className="text-lg sm:text-xl font-bold text-navy-900 mb-4" data-id="element-51">
                        ¿Cuál es tu fecha de nacimiento?
                      </h2>
                      <form onSubmit={handleDobSubmit} data-id="element-52">
                        <div className="mb-6 grid grid-cols-3 gap-2 sm:gap-3" data-id="element-53">
                          <select required value={formData.dobMonth} onChange={e => setFormData({
                      ...formData,
                      dobMonth: e.target.value
                    })} className="w-full px-2 sm:px-3 py-3 text-sm sm:text-base rounded-xl border-2 border-slate-200 focus:ring-0 focus:border-brandBlue-500 outline-none transition-colors bg-white text-slate-700 appearance-none cursor-pointer" data-id="element-54">
                            <option value="" disabled data-id="element-55">
                              Mes
                            </option>
                            <option value="01" data-id="element-56">Enero</option>
                            <option value="02" data-id="element-57">Febrero</option>
                            <option value="03" data-id="element-58">Marzo</option>
                            <option value="04" data-id="element-59">Abril</option>
                            <option value="05" data-id="element-60">Mayo</option>
                            <option value="06" data-id="element-61">Junio</option>
                            <option value="07" data-id="element-62">Julio</option>
                            <option value="08" data-id="element-63">Agosto</option>
                            <option value="09" data-id="element-64">Septiembre</option>
                            <option value="10" data-id="element-65">Octubre</option>
                            <option value="11" data-id="element-66">Noviembre</option>
                            <option value="12" data-id="element-67">Diciembre</option>
                          </select>
                          <select required value={formData.dobDay} onChange={e => setFormData({
                      ...formData,
                      dobDay: e.target.value
                    })} className="w-full px-2 sm:px-3 py-3 text-sm sm:text-base rounded-xl border-2 border-slate-200 focus:ring-0 focus:border-brandBlue-500 outline-none transition-colors bg-white text-slate-700 appearance-none cursor-pointer" data-id="element-68">
                            <option value="" disabled data-id="element-69">
                              Día
                            </option>
                            {Array.from({
                        length: 31
                      }, (_, i) => i + 1).map(d => <option key={d} value={String(d).padStart(2, '0')} data-id="element-70">
                                {d}
                              </option>)}
                          </select>
                          <select required value={formData.dobYear} onChange={e => setFormData({
                      ...formData,
                      dobYear: e.target.value
                    })} className="w-full px-2 sm:px-3 py-3 text-sm sm:text-base rounded-xl border-2 border-slate-200 focus:ring-0 focus:border-brandBlue-500 outline-none transition-colors bg-white text-slate-700 appearance-none cursor-pointer" data-id="element-71">
                            <option value="" disabled data-id="element-72">
                              Año
                            </option>
                            {Array.from({
                        length: 80
                      }, (_, i) => new Date().getFullYear() - 18 - i).map(y => <option key={y} value={String(y)} data-id="element-73">
                                {y}
                              </option>)}
                          </select>
                        </div>
                        <div className="flex gap-2" data-id="element-74">
                          <button type="button" onClick={() => setStep(1)} className="px-4 py-3 rounded-xl font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors text-sm" data-id="element-75">
                            Atrás
                          </button>
                          <button type="submit" className="flex-1 bg-brandBlue-600 hover:bg-brandBlue-700 text-white font-bold py-3 px-4 rounded-xl transition-colors shadow-lg shadow-brandBlue-600/30 text-sm sm:text-base" data-id="element-76">
                            Siguiente →
                          </button>
                        </div>
                        <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-slate-400" data-id="element-77">
                          <Lock className="w-3 h-3" data-id="element-78" />
                          <span data-id="element-79">
                            100% gratis. Sin compromisos. Tus datos están
                            seguros.
                          </span>
                        </div>
                      </form>
                    </motion.div>}

                  {/* STEP 3 — Contact Info (compact, no heading) */}
                  {step === 3 && <motion.div key="step3" {...stepAnim} data-id="element-80">
                      <form onSubmit={handleSubmit} className="space-y-2" data-id="element-81">
                        {/* First Name */}
                        <div data-id="element-82">
                          <input type="text" required placeholder="Nombre" value={formData.firstName} onChange={e => {
                      setFormData({
                        ...formData,
                        firstName: e.target.value
                      });
                      if (nameErrors.firstName) setNameErrors({
                        ...nameErrors,
                        firstName: ''
                      });
                    }} className={`w-full px-3 py-2.5 text-sm rounded-xl border-2 ${nameErrors.firstName ? 'border-red-400' : 'border-slate-200'} focus:ring-0 focus:border-brandBlue-500 outline-none transition-colors`} data-id="element-83" />
                          {nameErrors.firstName && <p className="text-red-500 text-xs mt-0.5 text-left" data-id="element-84">
                              {nameErrors.firstName}
                            </p>}
                        </div>

                        {/* Last Name */}
                        <div data-id="element-85">
                          <input type="text" required placeholder="Apellido" value={formData.lastName} onChange={e => {
                      setFormData({
                        ...formData,
                        lastName: e.target.value
                      });
                      if (nameErrors.lastName) setNameErrors({
                        ...nameErrors,
                        lastName: ''
                      });
                    }} className={`w-full px-3 py-2.5 text-sm rounded-xl border-2 ${nameErrors.lastName ? 'border-red-400' : 'border-slate-200'} focus:ring-0 focus:border-brandBlue-500 outline-none transition-colors`} data-id="element-86" />
                          {nameErrors.lastName && <p className="text-red-500 text-xs mt-0.5 text-left" data-id="element-87">
                              {nameErrors.lastName}
                            </p>}
                        </div>

                        {/* Phone with country code */}
                        <div className="flex gap-2" data-id="element-88">
                          <select value={formData.country} onChange={e => setFormData({
                      ...formData,
                      country: e.target.value
                    })} className="shrink-0 w-[100px] px-2 py-2.5 text-sm rounded-xl border-2 border-slate-200 focus:ring-0 focus:border-brandBlue-500 outline-none transition-colors bg-white text-slate-700 cursor-pointer" data-id="element-89">
                            <option value="US" data-id="element-90">US +1</option>
                            <option value="MX" data-id="element-91">MX +52</option>
                            <option value="CO" data-id="element-92">CO +57</option>
                            <option value="AR" data-id="element-93">AR +54</option>
                            <option value="ES" data-id="element-94">ES +34</option>
                            <option value="PE" data-id="element-95">PE +51</option>
                            <option value="CL" data-id="element-96">CL +56</option>
                            <option value="VE" data-id="element-97">VE +58</option>
                            <option value="EC" data-id="element-98">EC +593</option>
                            <option value="GT" data-id="element-99">GT +502</option>
                            <option value="DO" data-id="element-100">DO +1</option>
                            <option value="HN" data-id="element-101">HN +504</option>
                            <option value="SV" data-id="element-102">SV +503</option>
                            <option value="PR" data-id="element-103">PR +1</option>
                          </select>
                          <input type="tel" required inputMode="numeric" pattern="[0-9]*" placeholder="Ej: 305 555 1234" value={formData.phone} onChange={e => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setFormData({
                        ...formData,
                        phone: val
                      });
                    }} className="flex-1 px-3 py-2.5 text-sm rounded-xl border-2 border-slate-200 focus:ring-0 focus:border-brandBlue-500 outline-none transition-colors" data-id="element-104" />
                        </div>

                        {/* Email */}
                        <div data-id="element-105">
                          <input type="email" required placeholder="Tu correo principal" value={formData.email} onChange={e => setFormData({
                      ...formData,
                      email: e.target.value
                    })} className="w-full px-3 py-2.5 text-sm rounded-xl border-2 border-slate-200 focus:ring-0 focus:border-brandBlue-500 outline-none transition-colors" data-id="element-106" />
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-2 pt-0.5" data-id="element-107">
                          <button type="button" onClick={() => setStep(2)} className="px-4 py-2.5 rounded-xl font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors text-sm" data-id="element-108">
                            Atrás
                          </button>
                          <button type="submit" className="flex-1 bg-brandBlue-600 hover:bg-brandBlue-700 text-white font-bold py-2.5 px-4 rounded-xl transition-colors shadow-lg shadow-brandBlue-600/30 text-sm" data-id="element-109">
                            Ver mis opciones →
                          </button>
                        </div>

                        <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-400" data-id="element-110">
                          <ShieldCheck className="w-3 h-3" data-id="element-111" />
                          <span data-id="element-112">
                            100% gratis. Sin compromisos. Tus datos están
                            seguros.
                          </span>
                        </div>
                      </form>
                    </motion.div>}
                </AnimatePresence>
              </div>}
          </motion.div>
        </div>
      </section>
    </>;
}